import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { Briefcase, ArrowRight, CheckCircle, Calculator, Building, Receipt, Calendar, BookOpen, Phone } from 'lucide-react';

const services = [
  { icon: Briefcase, title: 'Self-Employed Tax Guidance', description: 'Navigate Schedule C, track expenses, maximize deductions, and stay compliant as a sole proprietor or freelancer.' },
  { icon: Building, title: 'LLC & S-Corp Tax Help', description: 'Entity selection guidance, business return preparation, and coordination between your personal and business filings.' },
  { icon: Calculator, title: 'Estimated Tax Planning', description: 'Quarterly estimated tax calculations and planning so you avoid underpayment penalties at year-end.' },
  { icon: Receipt, title: 'Deduction Questions', description: 'What can you write off? We help you identify legitimate deductions and avoid red flags that trigger audits.' },
  { icon: BookOpen, title: 'Bookkeeping-to-Tax Coordination', description: 'We work with your bookkeeper — or help you set up basic records — so your tax filings are accurate and efficient.' },
  { icon: Calendar, title: 'Year-Round Tax Support', description: 'Tax questions don\'t only happen in April. We provide ongoing support throughout the year as your business grows.' },
];

export default function SmallBusiness() {
  useSEO({
    title: 'Small Business Tax Help',
    description: 'Tax guidance for self-employed, LLC, and S-corp owners. Estimated taxes, deductions, bookkeeping coordination, and year-round support.',
  });

  return (
    <div data-testid="small-business-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
                Small Business Tax Help
              </p>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
                Tax Help Built for Business Owners
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                Self-employed, LLC, S-corp — whatever your structure, we provide the tax guidance and support your business needs to stay compliant and save money.
              </p>
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="small-biz-hero-cta"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
                >
                  Speak With a Tax Professional
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </AnimatedSection>
            <AnimatedSection delay={0.15} className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1758611972515-23399a8786df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxzZWxmJTIwZW1wbG95ZWQlMjBibGFjayUyMHdvbWFuJTIwZW50cmVwcmVuZXVyJTIwd29ya2luZyUyMG9mZmljZSUyMGxhcHRvcCUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzY0Njc3ODJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Small business owner"
                className="w-full h-80 object-cover rounded-lg"
                loading="lazy"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              How We Help
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Practical tax support for every stage of your business
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {services.map((service, i) => (
              <AnimatedSection key={i} delay={i * 0.07}>
                <div className="bg-white p-8 h-full">
                  <div className="w-11 h-11 rounded-md bg-brand-navy/5 flex items-center justify-center mb-5">
                    <service.icon className="w-5 h-5 text-brand-navy" />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-brand-navy mb-3">{service.title}</h3>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">{service.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Value Prop */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
              Better clients. Recurring work. Real relationships.
            </h2>
            <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-8">
              Small business tax help is more than a once-a-year filing. We build long-term relationships with business owners, providing guidance throughout the year so you can focus on running your business.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                'Proactive tax planning',
                'Quarterly check-ins available',
                'Entity structure optimization',
                'Coordination with your CPA or bookkeeper',
                'Plain-English explanations',
                '25+ years of business tax experience',
              ].map((point, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                  <span className="text-sm text-slate-700">{point}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Let's talk about your business
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Schedule a consultation to discuss your business tax needs. No obligation, just a clear conversation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md">
                  Book a Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="tel:8555892461">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-6 text-base rounded-md">
                  <Phone className="w-4 h-4 mr-2" /> 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
